import { SortDirection } from "global";
import { useForm } from "../../../hooks/useForm";
import {
  useDeleteVideoContentAsAdminMutation,
  useGetVideoContentsAsAdminQuery,
  VideoContentSearchType,
  VideoContentSortKey,
} from "../../../lib/apollo/graphql/generated";
import { formatDate } from "../../../utilities/format";
import BoxWithShadow from "../../common/components/BoxWithShadow";
import ListPageTopSection from "../../common/components/ListPageTopSection";
import SearchBar from "../../common/components/SearchBar";
import SelectBox from "../../common/components/SelectBox";
import Table from "../../common/components/Table";
import { getTotalPages } from "../../../utilities/pagination";
import { FETCH_SIZE } from "../../../utilities/constants";
import { Video } from "../container/VideosContainer";
import ActiveStatus from "../../common/components/ActiveStatus";

interface AddedVideoListProps {
  onVideoSelect: (video: Video) => void;
}

export default function AddedVideoList({ onVideoSelect }: AddedVideoListProps) {
  const [filters, onChange, onBatch] = useForm({
    page: 1,
    query: "",
    videoContentSearchType: VideoContentSearchType.Title,
    sortKey: VideoContentSortKey.CreatedAt,
    sortDirection: "DESC" as SortDirection,
  });

  const { data, refetch, loading } = useGetVideoContentsAsAdminQuery({
    variables: {
      limit: FETCH_SIZE,
      page: filters.page,
    },
  });

  const videos = data?.getVideoContentsAsAdmin.edges || [];
  const totalPages = getTotalPages(
    data?.getVideoContentsAsAdmin.pageInfo.totalCount
  );

  const onSearch = (updated: typeof filters) => {
    const pageOneFilters = {
      ...updated,
      page: 1,
    };

    onBatch(pageOneFilters);
    refetch(pageOneFilters);
  };

  const onSort = (key?: VideoContentSortKey, direction?: SortDirection) => {
    const updatedFilters = {
      ...filters,
      sortKey: key,
      sortDirection: direction,
    };

    onBatch(updatedFilters);
    refetch(updatedFilters);
  };

  const [deleteVideo, { loading: isDeleting }] =
    useDeleteVideoContentAsAdminMutation({
      onCompleted: () => {
        alert("동영상을 비활성화 했습니다.");
      },
      refetchQueries: ["GetVideoContentsAsAdmin"],
    });

  const onDelete = (videoId: string) => {
    if (
      !window.confirm("정말로 동영상을 비활성화 하시겠습니까?") ||
      !videoId ||
      isDeleting
    ) {
      return;
    }

    deleteVideo({
      variables: {
        videoContentId: videoId,
      },
    });
  };

  return (
    <BoxWithShadow className="gap-3">
      <ListPageTopSection>
        <SelectBox
          value={filters.videoContentSearchType}
          onSelect={(value) => {
            onChange("videoContentSearchType", value);
            onChange("query", "");
          }}
          className="w-1/3"
          options={[
            { label: "제목", value: VideoContentSearchType.Title },
            { label: "상태", value: VideoContentSearchType.Status },
          ]}
        />
        {filters.videoContentSearchType === VideoContentSearchType.Title ? (
          <SearchBar
            value={filters.query}
            onChange={(value) => onChange("query", value)}
            onSearch={() => onSearch(filters)}
          />
        ) : (
          <SelectBox
            value={filters.query}
            onSelect={(value) => {
              onChange("query", value);

              const updatedFilters = {
                ...filters,
                query: value,
                page: 1,
              };

              onSearch(updatedFilters);
            }}
            options={[
              { label: "운영중", value: "운영중" },
              { label: "대기중", value: "대기중" },
              { label: "미운영", value: "비운영" },
            ]}
            className="w-[250px]"
          />
        )}
      </ListPageTopSection>
      <Table.Wrapper>
        <Table.Header
          tabs={[
            { label: "썸네일", className: "w-[250px] flex-shrink-0" },
            {
              label: "제목",
              className: "w-[250px] flex-shrink-0",
              sortKey: VideoContentSortKey.Title,
            },
            { label: "카테고리", sortKey: VideoContentSortKey.Category },
            { label: "재생시간", sortKey: VideoContentSortKey.Playtime },
            { label: "조회수", sortKey: VideoContentSortKey.ViewCount },
            { label: "등록일", sortKey: VideoContentSortKey.CreatedAt },
            { label: "상태", sortKey: VideoContentSortKey.Status },
            { label: "관리" },
          ]}
          sort={{
            key: filters.sortKey,
            direction: filters.sortDirection,
            onSort,
          }}
        />
        <Table.Body isLoading={loading} isEmpty={!videos.length}>
          {videos?.map((video) => {
            return (
              <Table.Row key={video.id}>
                <Table.Column className="w-[250px] flex-shrink-0">
                  <img
                    src={
                      encodeURI(video.media?.uri) ||
                      "/images/default_thumbnail_image.png"
                    }
                    alt={video.title + " thumbnail"}
                    draggable={false}
                    className="h-28 rounded-md object-cover"
                  />
                </Table.Column>
                <Table.Column className="line-clamp-3 flex w-[250px] flex-shrink-0 justify-center text-wrap">
                  {video.title}
                </Table.Column>
                <Table.Column>{video.videoContentCategory.name}</Table.Column>
                <Table.Column>{video.playTime}</Table.Column>
                <Table.Column>
                  {video.viewCount?.toLocaleString()}회
                </Table.Column>
                <Table.Column>
                  {formatDate(video.createdAt, "YY.MM.DD")}
                </Table.Column>
                <Table.Column>
                  <ActiveStatus status={video.status.name} />
                </Table.Column>
                <Table.RowManageButtons
                  onUpdate={() =>
                    onVideoSelect({
                      videoContentId: video.id,
                      title: video.title,
                      description: video.description || "",
                      youtubeUrl: video.youtubeUrl,
                      playTime: video.playTime,
                      statusId: video.status.id,
                      videoContentCategoryId: video.videoContentCategory.id,
                      media: {
                        file: null,
                      },
                    })
                  }
                  onDelete={
                    video.status.name === "비운영"
                      ? undefined
                      : () => onDelete(video.id)
                  }
                />
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Wrapper>
      <Table.Pagination
        page={filters.page}
        total={totalPages}
        onPageChange={(newPage) => onChange("page", newPage)}
      />
    </BoxWithShadow>
  );
}
